import { gql } from '@apollo/client';
import type { SerializedFilter } from '@enmon/commons/lib/enmon';
import type { SerializedHardware } from '@enmon/commons/lib/tools';
import { Select, Skeleton } from 'antd';
import type { SelectProps } from 'antd/lib/select';
import { useStrapiQuery } from 'hooks/useStrapiQuery';
import { type VFC, useCallback, useMemo } from 'react';
import { filterOption } from '../utils';

export const GET_HARDWARE_DATA = gql`
  query getHardwaresData($sort: [String]) {
    hardwares(sort: $sort, pagination: { limit: -1 }) {
      data {
        id
        attributes {
          name
          category
          query
        }
      }
    }
  }
`;

export const GET_SIMPLE_HARDWARE_DATA = gql`
  query getHardwaresData($sort: [String]) {
    hardwares(sort: $sort, pagination: { limit: -1 }) {
      data {
        id
        attributes {
          name
        }
      }
    }
  }
`;

export interface HardwaresVariables {
  sort: string[];
}

export type HardwareDataItem = Pick<SerializedHardware, 'id' | 'name' | 'query' | 'category'>;

export interface HardwareData {
  hardwares: {
    data: HardwareDataItem[];
  };
}

export interface HardwaresSelectProps extends SelectProps<number> {
  useSimpleHardwareData?: boolean;
  filter?: SerializedFilter;
  filterHardwares?: (option: HardwareDataItem) => boolean;
  onChange?: (value: number | null) => void;
  onHardwareChange?: (value: HardwareDataItem | undefined) => void;
  setCustomerId?: (value: string | undefined) => void;
}

export const HardwaresSelect: VFC<HardwaresSelectProps> = ({
  useSimpleHardwareData,
  filter,
  filterHardwares = () => true,
  onChange,
  onHardwareChange,
  setCustomerId,
  ...propsToPass
}) => {
  const { data: { hardwares: { data: hardwares } = {} } = {}, loading: isLoading } = useStrapiQuery<
    HardwareData,
    HardwaresVariables
  >(useSimpleHardwareData ? GET_SIMPLE_HARDWARE_DATA : GET_HARDWARE_DATA, {
    variables: {
      sort: ['name:asc'],
    },
    fetchPolicy: 'cache-and-network',
  });

  const hardwaresOptions = useMemo(
    () => hardwares?.filter(filterHardwares).map(hardware => ({ value: hardware.id, label: hardware.name })),
    [hardwares, filterHardwares],
  );

  const handleChange = useCallback(
    (value: number | null) => {
      onChange?.(value);
      onHardwareChange?.(value ? hardwares?.find(hardware => hardware.id === value) : undefined);
    },
    [hardwares, onChange, onHardwareChange],
  );

  const handleClear = useCallback(() => {
    onChange?.(null);
    onHardwareChange?.(undefined);
  }, [onChange, onHardwareChange]);

  if (isLoading) return <Skeleton.Input active block />;

  return (
    <Select
      allowClear
      showSearch={{
        filterOption,
      }}
      placeholder="Select hardware"
      loading={isLoading}
      options={hardwaresOptions}
      virtual
      onChange={handleChange}
      onClear={handleClear}
      {...propsToPass}
    />
  );
};
